<template>
  <div class="subscription">
    <h1 class="title subscription-title">
      Subscription and credit card 
      <icon-button class="setting-button" icon="el-icon-setting" size="16" @click="showSettingsDialog = true" />
    </h1>

    <div class="pricing" v-if="!organizationLoading">
      <div class="price-item">
        {{ currentPricingModel.monthlyFee }} {{ organization.currency.name }}
        <span>Monthly fee</span>
      </div>
      <div class="price-item">
        {{ currentPricingModel.transactionFee }}%
        <span>Transaction fee</span>
      </div>
    </div>

    <div class="organization-card" v-if="card">
      <credit-card
        @click.native="showCCOptionDialog = true"
        header-color="bg"
        :card="card"
        :selectedId="card.id" />
    </div>
    <div class="organization-no-card" @click="showDialog = true" v-else>
      No credit card added
      <span>Click to add</span>
    </div>

    <el-dialog :append-to-body="true" title="Billing options" :visible.sync="showSettingsDialog" width="400px">
      <billing-options :organization="organization" @update="updateOrganization" v-if="showSettingsDialog" />
    </el-dialog>

    <el-dialog :append-to-body="true" :visible.sync="showCCOptionDialog" width="400px" custom-class="dialog-no-padding">
      <div class="cc-option" v-if="showCCOptionDialog" v-loading="removeCardLoading">
        <div class="cc-wrap cc-card">
          <credit-card
            class="cc-card"
            hide-caret
            header-color="bg"
            :card="card"
            :selectedId="card.id" />
        </div>
        <div class="controls">
          <el-button type="default" icon="el-icon-close" @click="httpRemoveCard(card)">Remove card</el-button>
          <el-button type="default" icon="el-icon-plus" @click="showNewCardDialog">Add new card</el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog :append-to-body="true" title="Add credit card" :visible.sync="showDialog" width="400px">
      <div class="add-cc-container">
        <transition name="fade">
          <div class="loader" v-if="loading">
            <grid-loader :done="httpSuccess" @complete="success" :error="status.failed"/>
          </div>
        </transition>
        <stripe-form
          v-if="showDialog"
          @success="retrieveToken" 
          @failed="retrieveTokenFailed"
          currency="usd"
          :getToken="getToken"
          class="top-margin"/>
        <el-button type="primary" icon="el-icon-upload" @click="getToken = true, loading = true">Add</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import CreditCard from '@/components/dashboard/saldoAndCredit/CreditCard'
import StripeForm from '@/components/dashboard/saldoAndCredit/stripe/Stripe'
import GridLoader from '@/components/shared/loaders/GridLoader'
import BillingOptions from './BillingOptions'

export default {
  name: 'Subscription',
  components: { CreditCard, StripeForm, GridLoader, BillingOptions },
  props: {
    organization: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      tempCard: {},
      card: this.organization.card,
      organizationLoading: true,
      removeCardLoading: false,
      showSettingsDialog: false,
      loading: false,
      showCCOptionDialog: false,
      showDialog: false,
      getToken: false,
      httpSuccess: false,
      error: null,
      status: {
        failed: false
      }
    }
  },
  computed: {
    currentPricingModel () {
      return this.organization.pricingModels.find(item => { return item.endedAt === null })
    }
  },
  watch: {
    organization (val) {
      if (val !== null) {
        this.organizationLoading = false
      }
      this.card = this.organization.card
    }
  },
  methods: {
    updateOrganization (organization) {
      this.showSettingsDialog = false
      this.$emit('updateOrganization', organization)
    },
    showNewCardDialog () {
      this.showCCOptionDialog = false
      setTimeout(() => {
        this.showDialog = true
      }, 400)
    },
    success () {
      this.showDialog = false
      this.loading = false
      this.card = this.tempCard
      this.httpSuccess = false
      this.status.failed = false
      this.$message.success('Card added successfully')
    },
    retrieveTokenFailed () {

    },
    async httpRemoveCard (card) {
      this.removeCardLoading = true
      try {
        const response = await this.$http.delete(this.$api.payments.cards.remove(card.id) + '?orgId=' + this.organization.id)
        this.card = null
        this.showCCOptionDialog = false
        this.$message('Credit card removed')
      } catch (e) {
        this.$message('Could not remove card')
      }
      this.removeCardLoading = false
    },
    async retrieveToken (token) {
      this.getToken = false
      let tokenObj = token.token
      const data = {
        brand: tokenObj.card.brand,
        expirationMonth: tokenObj.card.exp_month,
        expirationYear: tokenObj.card.exp_year,
        last4: tokenObj.card.last4,
        token: tokenObj.card.id
      }
      try {
        const response = await this.$http.post(this.$api.payments.cards.save(tokenObj.id) + '&orgId=' + this.organization.id, data)
        this.httpSuccess = true
        this.tempCard = response.data
      } catch (e) {
        this.$message.error('Card could not be added')
        this.status.failed
        this.error = e.response.data['']
        this.httpSuccess = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .subscription-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .cc-option {
    background-color: white;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .cc-card {
      width: 100%;
      margin-bottom: 0px;
    }

    .cc-wrap {
      overflow: hidden;
    }

    .controls {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      button {
        flex: 1;
        margin: 0;
        border-radius: 0px;
      }
    }
  }

  .pricing {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 0px;

    .price-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      color: $main;
      margin: 0px 20px;

      span {
        font-size: 12px;
        color: $text-light;
      }
    }
  }

  .add-cc-container {
    padding: 15px 20px 20px 20px;

    .loader {
      z-index: 9999999999999999999;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      background-color: white;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    button {
      width: 100%;
      margin-top: 20px;
    }
  }

  .subscription {
    width: 100%;
    box-sizing: border-box;
    padding: 40px;
  }

  .organization-no-card {
    cursor: pointer;
    border: 1px dashed $border;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    height: 160px;
    background-color: white;
    transition: border-color .15s linear;

    color: $text-light;
    font-size: 14px;

    &:hover {
      border-color: rgba($main, .8);
    }

    span {
      font-size: 12px;
      color: $text-light;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/organizations/organization/Subscription.vue